import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, Target, Users, Award, CheckCircle2, Heart, Shield, Star, ExternalLink, ArrowRight, Eye, Lightbulb
} from 'lucide-react';
import { useSiteData } from '../contexts/SiteDataContext';
import SEO from '../components/SEO';
import { 
  PageHeader, 
  Section, 
  Container, 
  SectionHeader,
  FadeInOnScroll,
  PrimaryButton
} from '../components/DesignSystem';

const About = () => {
  const { 
    companyInfo, 
    siteSettings, 
    getAccreditationsFor,
    companyName,
    loading 
  } = useSiteData();

  // Get CMS data with fallbacks
  const aboutPage = siteSettings?.aboutPage || {};
  const metaTitle = aboutPage.metaTitle || `About Us - ${companyName || 'FURD Ltd'}`;
  const metaDescription = aboutPage.metaDescription || 'Learn about FURD Ltd, a leading MEP engineering consultancy';
  const heroTitle = aboutPage.heroTitle || `About ${companyName || 'FURD'}`;
  const heroSubtitle = aboutPage.heroSubtitle || companyInfo?.tagline || 'Building our future by engineering projects with generational value';
  
  const aboutDescription = siteSettings?.aboutDescription || 
    `Founded in 2021, ${companyName || 'FURD Ltd'} has quickly established itself as a trusted MEP engineering consultancy. We specialise in delivering comprehensive mechanical, electrical, and public health engineering solutions across a diverse range of projects.`;
  const vision = siteSettings?.vision || 
    'To be the UK\'s most trusted MEP consultancy, known for engineering excellence and genuine partnership with our clients.';
  const mission = siteSettings?.mission || 
    'To deliver outstanding building services engineering through technical expertise, collaborative working, and unwavering commitment to quality.';
  const aboutImage = siteSettings?.aboutImage || '';
  
  // Get values from CMS
  const values = siteSettings?.values?.length > 0 ? siteSettings.values : [
    { name: 'Collaboration', description: 'Working alongside clients, contractors, and stakeholders as equal partners throughout every project phase.' },
    { name: 'Accountability', description: 'Taking full ownership of our designs and recommendations — standing behind every calculation and specification.' },
    { name: 'Integrity', description: 'Providing honest, technically sound advice that prioritises long-term building performance over short-term gains.' },
    { name: 'Excellence', description: 'Pursuing the highest standards in technical design, documentation, and project delivery.' }
  ];
  
  // Value icons mapping
  const valueIcons = {
    'Collaboration': Users,
    'Accountability': Shield,
    'Integrity': Heart,
    'Excellence': Star
  };
  
  // Get accreditations that should display on the About page
  const accreditations = getAccreditationsFor('about');
  const hasAccreditations = accreditations.length > 0;
  const accreditationsTitle = aboutPage.accreditationsTitle || 'Memberships & Accreditations';
  const accreditationsSubtitle = aboutPage.accreditationsSubtitle || 'Certified excellence in MEP engineering';

  // Fallback accreditations if none configured
  const displayAccreditations = hasAccreditations ? accreditations : [
    { name: 'ISO 9001:2015', description: 'Quality Management System', certificate: 'Certificate No: 431832023' },
    { name: 'Alcumus SafeContractor', description: 'Health & Safety Accredited', certificate: '' }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <SEO 
        title={metaTitle}
        description={metaDescription}
      />
      
      {/* Hero Section using Design System */}
      <PageHeader
        badge="Our Story"
        badgeIcon={Building2}
        title={heroTitle}
        subtitle={heroSubtitle}
      />

      {/* About Content - Enhanced with animations */}
      <Section background="white" padding="default">
        <Container>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image or Decorative Element */}
            <FadeInOnScroll direction="left">
              <div className="relative">
                {aboutImage ? (
                  <div className="relative group">
                    <div className="absolute -inset-4 bg-gradient-to-br from-blue-600/20 to-blue-400/10 rounded-3xl blur-2xl opacity-60 group-hover:opacity-100 transition-opacity" />
                    <img 
                      src={aboutImage} 
                      alt={`About ${companyName}`}
                      className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="relative group">
                    <div className="absolute -inset-4 bg-gradient-to-br from-blue-600/20 to-blue-400/10 rounded-3xl blur-2xl opacity-60" />
                    <div className="relative bg-gradient-to-br from-slate-100 to-blue-50 rounded-3xl h-[500px] flex items-center justify-center overflow-hidden">
                      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
                      <div className="text-center p-8 relative z-10">
                        <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                          <Building2 className="w-12 h-12 text-white" />
                        </div>
                        <p className="text-2xl font-bold text-gray-900">MEP Engineering Excellence</p>
                        <p className="text-gray-500 mt-2">Since 2021</p>
                      </div>
                    </div>
                  </div>
                )}
                {/* Floating accent with animation */}
                <div className="absolute -bottom-6 -right-6 w-28 h-28 bg-gradient-to-br from-blue-600 to-blue-500 rounded-2xl shadow-2xl flex items-center justify-center animate-float">
                  <Target className="w-14 h-14 text-white" />
                </div>
              </div>
            </FadeInOnScroll>
            
            {/* Content */}
            <FadeInOnScroll direction="right" delay={150}>
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600/10 to-blue-500/10 rounded-full text-blue-600 font-semibold text-sm uppercase tracking-widest mb-6 border border-blue-200/50">
                  <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
                  Who We Are
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                  Engineering Excellence, <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-500">Delivered</span>
                </h2>
                <p className="text-lg text-gray-500 mb-10 leading-relaxed whitespace-pre-wrap">
                  {aboutDescription}
                </p>
                
                {/* Stats with enhanced styling */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300">
                    <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-500 mb-1">2021</p>
                    <p className="text-gray-500 font-medium">Founded</p>
                  </div>
                  <div className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300">
                    <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-500 mb-1">UK</p>
                    <p className="text-gray-500 font-medium">Wide Service</p>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </Container>
      </Section>

      {/* Animated float keyframe */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float { animation: float 3s ease-in-out infinite; }
      `}</style>

      {/* Vision & Mission - Enhanced with animations */}
      <Section background="dark" padding="default">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-[200px] -right-[200px] w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-[150px] -left-[150px] w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-3xl" />
        </div>
        
        <Container className="relative z-10">
          <FadeInOnScroll>
            <div className="text-center mb-16">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full text-blue-300 font-semibold text-sm uppercase tracking-widest mb-6 border border-blue-400/20">
                <Lightbulb className="w-4 h-4" />
                Our Purpose
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">
                Driven by <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-300">Vision</span>, Guided by <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-300">Mission</span>
              </h2>
            </div>
          </FadeInOnScroll>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Vision */}
            <FadeInOnScroll delay={100} direction="left">
              <div className="group bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-300 h-full">
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-400 rounded-2xl blur-lg opacity-50 group-hover:opacity-70 transition-opacity" />
                  <div className="relative w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-400 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
                    <Eye className="w-8 h-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Our Vision</h3>
                <p className="text-blue-100/80 leading-relaxed text-lg whitespace-pre-wrap">{vision}</p>
              </div>
            </FadeInOnScroll>
            
            {/* Mission */}
            <FadeInOnScroll delay={200} direction="right">
              <div className="group bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-300 h-full">
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-400 rounded-2xl blur-lg opacity-50 group-hover:opacity-70 transition-opacity" />
                  <div className="relative w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-400 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Our Mission</h3>
                <p className="text-blue-100/80 leading-relaxed text-lg whitespace-pre-wrap">{mission}</p>
              </div>
            </FadeInOnScroll>
          </div>
        </Container>
      </Section>

      {/* Values - Enhanced with staggered animations */}
      <Section background="gradient" padding="default">
        <Container>
          <SectionHeader
            badge="What Drives Us"
            title="Our Core"
            titleHighlight="Values"
            subtitle="The principles that guide every decision and every project we undertake."
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {values.map((value, index) => {
              const Icon = valueIcons[value.name] || CheckCircle2;
              return (
                <FadeInOnScroll key={index} delay={index * 100}>
                  <div className="group h-full bg-white/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100/80 hover:border-blue-200/80 transform hover:-translate-y-2 relative overflow-hidden">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-600/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    <div className="relative mb-5">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-400 rounded-xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity" />
                      <div className="relative w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-2 transition-all duration-500 shadow-lg">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300 relative z-10">{value.name}</h3>
                    <p className="text-gray-500 leading-relaxed relative z-10">{value.description}</p>
                  </div>
                </FadeInOnScroll>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* Accreditations Section - Enhanced */}
      <Section background="white" padding="default">
        <Container>
          <SectionHeader
            badge="Certifications"
            title={accreditationsTitle}
            subtitle={accreditationsSubtitle}
          />
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {displayAccreditations.map((acc, index) => (
              <FadeInOnScroll key={acc.id || index} delay={index * 75}>
                <div className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 flex flex-col items-center text-center border border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all duration-300 h-full">
                  {acc.logo ? (
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <img 
                        src={acc.logo} 
                        alt={acc.name}
                        className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.nextSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="hidden w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-500 rounded-full flex items-center justify-center">
                        <Award className="w-8 h-8 text-white" />
                      </div>
                    </div>
                  ) : (
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl flex items-center justify-center mb-4 group-hover:from-blue-600 group-hover:to-blue-500 transition-all duration-300">
                      <Award className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                  )}
                  <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{acc.name}</h3>
                  {acc.description && <p className="text-sm text-gray-500 mt-1">{acc.description}</p>}
                  {acc.certificate && <p className="text-xs text-gray-400 mt-1">{acc.certificate}</p>}
                  {acc.link && (
                    <a 
                      href={acc.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm mt-3 font-medium group-hover:gap-2 transition-all"
                    >
                      Learn more <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section background="dark" padding="default">
        <Container className="text-center relative z-10">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-[200px] -right-[200px] w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-[150px] -left-[150px] w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-3xl" />
          </div>
          
          <FadeInOnScroll>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">Ready to Work With Us?</h2>
            <p className="text-lg md:text-xl text-blue-100/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              Let&apos;s discuss how our expertise can help bring your next project to life.
            </p>
            <PrimaryButton to="/contact" size="lg" icon={ArrowRight}>
              Get in Touch
            </PrimaryButton>
          </FadeInOnScroll>
        </Container>
      </Section>
    </div>
  );
};

export default About;
